/**
 * A concurrent request queue with priority support and auto-retry.
 *
 * Requests are executed up to `maxConcurrency` at a time to balance
 * throughput with backend capacity. Higher-priority requests (e.g., the
 * active tab) are moved to the front.
 * Failed requests are automatically retried once before reporting an error.
 */

type QueueEntry<T = unknown> = {
    id: string;
    priority: number; // lower = higher priority
    fetcher: () => Promise<T>;
    resolve: (value: T) => void;
    reject: (error: Error) => void;
    cancelled: boolean;
};

export class RequestQueue {
    private queue: QueueEntry[] = [];
    private activeCount = 0;
    private readonly maxConcurrency: number;

    constructor(maxConcurrency = 1) {
        this.maxConcurrency = maxConcurrency;
    }

    /**
     * Enqueue a request. Returns a promise that resolves with the result.
     * If a request with the same id is already queued (not yet running),
     * the old one is cancelled and replaced.
     */
    enqueue<T>(
        id: string,
        fetcher: () => Promise<T>,
        priority: number = 10
    ): { promise: Promise<T>; cancel: () => void } {
        // Cancel any existing queued (not running) entry with same id
        for (const entry of this.queue) {
            if (entry.id === id) {
                entry.cancelled = true;
            }
        }

        let resolve!: (value: T) => void;
        let reject!: (error: Error) => void;
        const promise = new Promise<T>((res, rej) => {
            resolve = res;
            reject = rej;
        });

        const entry: QueueEntry<T> = {
            id,
            priority,
            fetcher,
            resolve: resolve as (value: unknown) => void,
            reject: reject as (error: Error) => void,
            cancelled: false,
        };

        this.queue.push(entry as QueueEntry);
        this.queue.sort((a, b) => a.priority - b.priority);
        this.drain();

        return {
            promise,
            cancel: () => {
                entry.cancelled = true;
            },
        };
    }

    /**
     * Boost priority for a given id — moves it towards the front of the queue.
     * Useful when the user switches to a tab whose data is still queued.
     */
    prioritize(id: string, newPriority: number = 0): void {
        for (const entry of this.queue) {
            if (entry.id === id && !entry.cancelled) {
                entry.priority = newPriority;
            }
        }
        this.queue.sort((a, b) => a.priority - b.priority);
    }

    private drain(): void {
        while (this.queue.length > 0 && this.activeCount < this.maxConcurrency) {
            const entry = this.queue.shift()!;
            if (entry.cancelled) continue;

            this.activeCount++;
            this.processEntry(entry).finally(() => {
                this.activeCount--;
                this.drain();
            });
        }
    }

    private async processEntry(entry: QueueEntry): Promise<void> {
        try {
            const result = await entry.fetcher();
            if (!entry.cancelled) entry.resolve(result);
        } catch (_err) {
            if (entry.cancelled) return;
            // Auto-retry once
            try {
                const result = await entry.fetcher();
                if (!entry.cancelled) entry.resolve(result);
            } catch (retryErr) {
                if (!entry.cancelled) {
                    entry.reject(retryErr instanceof Error ? retryErr : new Error(String(retryErr)));
                }
            }
        }
    }
}

// Singleton queue for component-tab requests.
// Concurrency=2: backend serializes DuckDB queries via lock, so more parallel
// requests just queue in-process. Keep concurrency low to avoid overwhelming
// a cold backend while still allowing some overlap with network latency.
const componentQueue = new RequestQueue(2);

// Bounded queue for normal-period dashboard loads (default 8w, etc.).
// Concurrency=3: matches Cloud Run max_instance_request_concurrency so one
// browser can fill a box with prioritized work (KPIs first, filter-counts later)
// without unbounded fan-out. Set in 0b16e4b3 — do not lower casually; use API
// retries for "no available instance", not another concurrency flip-flop.
const normalDashboardQueue = new RequestQueue(3);

// Serialized queue for long-period dashboard loads (FY, 52w, etc.).
const heavyDashboardQueue = new RequestQueue(1);

export function getComponentQueue(): RequestQueue {
    return componentQueue;
}

export function getDashboardQueue(heavy = false): RequestQueue {
    return heavy ? heavyDashboardQueue : normalDashboardQueue;
}
